import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ExercisePayload, Locale } from '@diana/shared'
import { api, type LessonExercise, type LessonFigure } from '../../../lib/api.js'
import { Markdown } from '../Markdown.js'
import { Button } from '../../../classroom/ui.js'
import { FigureC } from './FigureC.js'
import {
  GAP,
  GAP_JOIN,
  cleanPrompt,
  countWords,
  gapCount,
  gapState,
  isAlmost,
  joinTiles,
  shownAnswer,
  splitGaps,
  stripTrailingGap,
  wordTiles,
} from './answers.js'
import { usePlayer, usePrimary, useStack, useStrip, type CheckResult, type ExerciseMemory } from './context.js'
import type { PlayerStrings } from './copy.js'

/*
 * One exercise, any of the eleven types, in the five states the spec sheets
 * draw (LP-Sheet-1/2): empty, answered, checked right, checked wrong (with
 * the right answer and the key's note), and „almost” when only the Polish
 * letters are off. Every markable type is a list of items each answered
 * with one string, so there is one submit path — api.course.answer, graded
 * on the server — and the type only picks the control.
 */

type ItemState = 'idle' | 'answered' | 'correct' | 'wrong' | 'almost'

type Items = Exclude<ExercisePayload, { type: 'unknown' }>['items']

function itemsOf(p: ExercisePayload): Items {
  return p.type === 'unknown' ? [] : p.items
}

export function ExerciseStep({
  exercise,
  figures,
  onChecked,
  bare = false,
}: {
  exercise: LessonExercise
  figures: LessonFigure[]
  onChecked?: () => void
  /** The page draws the header itself (homework parts). */
  bare?: boolean
}) {
  const { locale, t } = usePlayer()
  const prompt = exercise.prompt[locale] || exercise.prompt.pl

  return (
    <div className="flex flex-col">
      {!bare && (
        <>
          <span className="text-class-label font-semibold text-class-ink-faint">
            {t.exercise} {exercise.ref}
          </span>
          {exercise.title && <h1 className="cl-heading m-0 mt-1.5">{exercise.title}</h1>}
          {prompt && <p className="m-0 mt-1 text-class-gloss text-class-ink-soft">{prompt}</p>}
        </>
      )}
      {exercise.attempt?.stale && (
        <p className="m-0 mt-3 border-l-2 border-class-gold-line pl-3 text-class-gloss text-class-ink-soft">{t.ex.stale}</p>
      )}
      {exercise.lead && exercise.type !== 'open' && exercise.type !== 'unknown' && (
        <div className="mt-4 text-class-ink-soft [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
          <Markdown variant="classroom">{exercise.lead}</Markdown>
        </div>
      )}
      {figures.map((f) => (
        <FigureC key={f.n} figure={f} />
      ))}
      <div className={bare ? 'mt-3' : 'mt-6'}>
        {exercise.type === 'unknown' ? (
          <RawTask exercise={exercise} t={t} />
        ) : exercise.type === 'open' ? (
          <OpenTask exercise={exercise} t={t} />
        ) : (
          <Markable exercise={exercise} locale={locale} t={t} onChecked={onChecked} />
        )}
      </div>
    </div>
  )
}

/* ── markable types ─────────────────────────────────────────────────── */

function Markable({
  exercise,
  locale,
  t,
  onChecked,
}: {
  exercise: LessonExercise
  locale: Locale
  t: PlayerStrings
  onChecked?: () => void
}) {
  const { memory, next, teacher } = usePlayer()
  const queryClient = useQueryClient()
  const payload = exercise.payload
  const items = itemsOf(payload)
  // Teacher view, key shown: read-only — the student's answers marked, the key under them.
  const review = teacher?.keyShown ? teacher : null
  const key = exercise.key

  const [mem, setMemState] = useState<ExerciseMemory>(() => {
    if (review) {
      const a = exercise.attempt
      return {
        answers: items.map((_, i) => String(a?.answers[i] ?? '')),
        result: a && key ? { correct: items.map((_, i) => a.items?.[i] === true), score: a.score, max: a.max, solution: key.solution } : null,
        locked: items.map(() => false),
      }
    }
    return (
      memory.get(exercise.id) ?? {
        answers: items.map((_, i) => exercise.attempt?.answers[i] ?? ''),
        result: null,
        locked: items.map(() => false),
      }
    )
  })
  const setMem = (m: ExerciseMemory) => {
    memory.set(exercise.id, m)
    setMemState(m)
  }

  const submit = useMutation({
    mutationFn: (answers: string[]) => api.course.answer(exercise.id, answers),
    onSuccess: (r) => {
      setMem({ ...mem, result: { correct: r.correct, score: r.score, max: r.max, solution: r.solution } })
      onChecked?.()
      queryClient.invalidateQueries({ queryKey: ['course'] })
    },
  })

  const set = (i: number, value: string) => {
    if (mem.result || mem.locked[i]) return
    setMem({ ...mem, answers: mem.answers.map((a, j) => (j === i ? value : a)) })
  }

  const state = (i: number): ItemState => {
    if (mem.locked[i]) return 'correct'
    const r = mem.result
    if (r) {
      if (r.correct[i]) return 'correct'
      const sol = r.solution.find((s) => s.n === items[i]?.n)
      if (sol && isAlmost(mem.answers[i] ?? '', sol.answers)) return 'almost'
      return 'wrong'
    }
    return (mem.answers[i] ?? '').trim() ? 'answered' : 'idle'
  }

  const answered = mem.answers.some((a, i) => !mem.locked[i] && a.trim())
  const wrong = mem.result ? items.map((_, i) => i).filter((i) => !mem.result!.correct[i] && !mem.locked[i]) : []

  const retry = () => {
    const r = mem.result
    if (!r) return
    const locked = items.map((_, i) => mem.locked[i] || r.correct[i] === true)
    setMem({ answers: mem.answers.map((a, i) => (locked[i] ? a : '')), result: null, locked })
  }

  const stack = useStack()
  usePrimary(
    stack
      ? null
      : review || mem.result
        ? { label: `${t.next} ›`, onClick: next }
        : { label: t.check, disabled: !answered || submit.isPending, onClick: () => submit.mutate(mem.answers), skip: !answered },
  )

  // Stacked (homework): hand the check to the page and report the state up.
  const check = useRef(() => {})
  check.current = () => {
    if (!mem.result && answered && !submit.isPending) submit.mutate(mem.answers)
  }
  useEffect(() => {
    if (!stack) return
    return stack.register(exercise.id, () => check.current())
  }, [stack, exercise.id])
  const doneBefore = Boolean(exercise.attempt && !exercise.attempt.stale)
  const scoreNow = mem.result ? items.filter((_, i) => mem.result!.correct[i] || mem.locked[i]).length : 0
  useEffect(() => {
    stack?.report(exercise.id, {
      answered,
      checked: Boolean(mem.result),
      done: Boolean(mem.result) || doneBefore,
      score: mem.result ? scoreNow : exercise.attempt?.score ?? 0,
      max: mem.result ? items.length : exercise.attempt?.max ?? 0,
      wrong: mem.result ? items.length - scoreNow : 0,
    })
  }, [stack, exercise.id, answered, mem.result, doneBefore, scoreNow, items.length, exercise.attempt])

  const r = mem.result
  const reviewed = r ? items.filter((_, i) => r.correct[i]).length : 0
  useStrip(
    stack ? null : review
      ? review.student
        ? r
          ? { tone: reviewed === items.length ? 'ok' : 'bad', text: `${review.student}: ${reviewed === items.length ? '✓' : '✗'}`, score: `${reviewed} / ${items.length}` }
          : { tone: 'neutral', text: `${review.student}: jeszcze bez odpowiedzi` }
        : null
      : submit.isError
      ? { tone: 'bad', text: `✗ ${t.ex.failed}` }
      : r
        ? {
            tone: wrong.length === 0 ? 'ok' : 'bad',
            text: wrong.length === 0 ? t.ex.allRight : t.ex.tryAgain,
            score: `${items.filter((_, i) => r.correct[i] || mem.locked[i]).length} / ${items.length}`,
            action: wrong.length > 0 ? { label: t.ex.fixWrong(wrong.length), onClick: retry } : undefined,
          }
        : null,
    `${review ? `v:${review.student}` : ''}${r ? 'r' : 'n'}:${wrong.length}:${submit.isError}`,
  )

  const solutionOf = (i: number) => r?.solution.find((s) => s.n === items[i]?.n)
  const disabled = Boolean(r) || submit.isPending || Boolean(review)

  // gap_bank: the gap a tapped word goes into.
  const [focus, setFocus] = useState<{ item: number; gap: number } | null>(null)

  return (
    <div className="flex flex-col">
      {payload.type === 'gap_bank' && payload.passage && <Passage md={payload.passage} />}
      {payload.type === 'gap' && payload.passage && <Passage md={payload.passage} />}
      {payload.type === 'gap_bank' && payload.bank.length > 0 && (
        <WordBank
          label={t.ex.bank}
          words={payload.bank}
          used={mem.answers.flatMap((a) => a.split(GAP_JOIN)).map((w) => w.trim())}
          disabled={disabled}
          onPick={(w) => {
            const target = focus ?? firstEmptyGap(items, mem.answers, mem.locked)
            if (!target) return
            const n = Math.max(1, gapCount(items[target.item]?.prompt ?? ''))
            const parts = splitGaps(mem.answers[target.item] ?? '', n)
            parts[target.gap] = w
            set(target.item, parts.join(GAP_JOIN))
            setFocus(null)
          }}
        />
      )}
      {payload.type === 'match' && payload.bank.length > 0 && <BankLegend bank={payload.bank} />}

      <ol className="m-0 list-none p-0">
        {items.map((item, i) => (
          <li key={item.n} className="flex flex-col gap-3.5 border-t border-class-line py-6 first:border-t-0 first:pt-0">
            <ItemBody
              payload={payload}
              item={item}
              index={i}
              value={mem.answers[i] ?? ''}
              onChange={(v) => set(i, v)}
              state={state(i)}
              disabled={disabled || mem.locked[i] === true}
              locale={locale}
              t={t}
              solution={solutionOf(i)?.answers ?? []}
              onFocusGap={(gap) => setFocus({ item: i, gap })}
            />
            <Feedback state={state(i)} solution={solutionOf(i)} locked={mem.locked[i] === true} t={t} show={(a) => answerText(payload, i, a, t)} />
            {review && !r && key && <KeyLine solution={key.solution.find((x) => x.n === item.n)} show={(a) => answerText(payload, i, a, t)} />}
          </li>
        ))}
      </ol>
      {stack && wrong.length > 0 && (
        <button type="button" onClick={retry} className="cl-link self-start text-class-gloss">
          {t.ex.fixWrong(wrong.length)} ›
        </button>
      )}
      {stack && submit.isError && <p className="m-0 text-class-gloss font-bold text-class-wrong">✗ {t.ex.failed}</p>}
    </div>
  )
}

function firstEmptyGap(items: Items, answers: string[], locked: boolean[]): { item: number; gap: number } | null {
  for (let i = 0; i < items.length; i++) {
    if (locked[i]) continue
    const n = Math.max(1, gapCount(items[i]!.prompt))
    const parts = splitGaps(answers[i] ?? '', n)
    const g = parts.findIndex((p) => !p.trim())
    if (g >= 0) return { item: i, gap: g }
  }
  return null
}

function Passage({ md }: { md: string }) {
  return (
    <div className="mb-6 rounded-class-md bg-class-white px-5 py-1 shadow-[inset_0_0_0_1px_var(--color-class-hairline)]">
      <Markdown variant="classroom">{md}</Markdown>
    </div>
  )
}

/** An answer as the student saw it: the option behind a letter, the column entry, „P · prawda”. */
function answerText(p: ExercisePayload, i: number, answer: string, t: PlayerStrings): string {
  const a = answer.trim()
  if (p.type === 'choice') {
    const at = 'abcdefgh'.indexOf(a.toLowerCase())
    const opt = p.options[i]?.[at]
    return opt ? `${a}) ${opt}` : a
  }
  if (p.type === 'match') return p.bank.find((e) => e.slice(0, 1).toUpperCase() === a.toUpperCase()) ?? a
  if (p.type === 'true_false') return a.toUpperCase() === 'P' ? `P · ${t.ex.true}` : a.toUpperCase() === 'F' ? `F · ${t.ex.false}` : a
  return a.split(GAP_JOIN.trim()).map((x) => x.trim()).join(' … ')
}

/** Teacher view, nothing answered yet: the key itself. */
function KeyLine({ solution, show }: { solution: { answers: string[]; note: string } | undefined; show: (answer: string) => string }) {
  if (!solution || solution.answers.length === 0) return null
  return (
    <p className="m-0 text-class-gloss">
      <span className="font-bold text-class-sky-700">Klucz: {show(shownAnswer(solution.answers))}</span>{' '}
      {solution.note && <span className="text-class-ink-soft">{solution.note}</span>}
    </p>
  )
}

function Feedback({
  state,
  solution,
  locked,
  t,
  show,
}: {
  state: ItemState
  solution: { answers: string[]; note: string } | undefined
  locked: boolean
  t: PlayerStrings
  show: (answer: string) => string
}) {
  if (locked || !solution) return null
  if (state === 'correct') {
    return solution.note ? (
      <p className="m-0 text-class-gloss text-class-ink-soft">
        <span className="font-bold text-class-correct">✓ {t.ex.good}</span> {solution.note}
      </p>
    ) : null
  }
  if (state === 'almost') {
    return (
      <p className="m-0 text-class-gloss font-bold text-class-gold-700">
        {t.ex.almost} {show(shownAnswer(solution.answers))}
      </p>
    )
  }
  if (state === 'wrong') {
    return (
      <p className="m-0 text-class-gloss">
        <span className="font-bold text-class-wrong">
          ✗ {t.ex.correctIs} {show(shownAnswer(solution.answers))}
        </span>{' '}
        {solution.note && <span className="text-class-ink-soft">{solution.note}</span>}
      </p>
    )
  }
  return null
}

/** Number + prompt line, the way every item starts. */
function PromptLine({ n, children, plain = false, state }: { n: number; children: ReactNode; plain?: boolean; state?: ItemState }) {
  return (
    <p lang="pl" className={['m-0 leading-snug', plain ? '' : 'font-class-display text-class-subheading'].join(' ')}>
      <ItemMark n={n} state={state} />
      {children}
    </p>
  )
}

/** The item's number; once checked, ✓ or ✗ in a soft circle (Exercises.png). */
function ItemMark({ n, state }: { n: number; state?: ItemState }) {
  if (state === 'correct' || state === 'wrong' || state === 'almost') {
    const tone =
      state === 'correct'
        ? 'bg-class-correct-100 text-class-correct'
        : state === 'almost'
          ? 'bg-class-gold-100 text-class-gold-700'
          : 'bg-class-wrong-100 text-class-wrong'
    return (
      <span
        aria-label={String(n)}
        className={`mr-2.5 inline-flex h-[22px] w-[22px] translate-y-[-2px] items-center justify-center rounded-full align-middle font-class-text text-class-label font-bold ${tone}`}
      >
        {state === 'correct' ? '✓' : state === 'almost' ? '~' : '✗'}
      </span>
    )
  }
  return <span className="mr-2.5 font-class-text text-class-gloss font-semibold text-class-ink-faint">{n}</span>
}

const stateClass: Record<ItemState, string> = {
  idle: '',
  answered: 'is-selected',
  correct: 'is-correct',
  wrong: 'is-wrong',
  almost: 'is-almost',
}

type BodyProps = {
  payload: ExercisePayload
  item: Items[number]
  index: number
  value: string
  onChange: (v: string) => void
  state: ItemState
  disabled: boolean
  locale: Locale
  t: PlayerStrings
  solution: string[]
  onFocusGap: (gap: number) => void
}

function ItemBody(props: BodyProps) {
  const { payload, item, index, locale } = props
  switch (payload.type) {
    case 'true_false':
      return (
        <>
          <PromptLine n={item.n} state={props.state}>{cleanPrompt(item.prompt, 'true_false', locale)}</PromptLine>
          <Tiles
            {...props}
            options={[
              { value: 'P', key: 'P', label: props.t.ex.true },
              { value: 'F', key: 'F', label: props.t.ex.false },
            ]}
            inline
          />
        </>
      )
    case 'choice': {
      const opts = payload.options[index] ?? []
      const letters = 'abcdefgh'
      const short = opts.length <= 3 && opts.every((o) => o.length <= 30)
      return (
        <>
          <PromptLine n={item.n} state={props.state}>{cleanPrompt(item.prompt, 'choice', locale, opts)}</PromptLine>
          <Tiles {...props} options={opts.map((o, j) => ({ value: letters[j]!, key: letters[j]!, label: o }))} grid={short} />
        </>
      )
    }
    case 'odd_one_out': {
      const fromPrompt = item.prompt.split(/\s+·\s+/).map((w) => w.trim()).filter(Boolean)
      const opts = (payload.options[index]?.length ? payload.options[index] : fromPrompt) ?? []
      return (
        <>
          <PromptLine n={item.n} state={props.state} plain>
            {''}
          </PromptLine>
          <Tiles {...props} options={opts.map((o) => ({ value: o, label: o }))} inline />
        </>
      )
    }
    case 'match':
      return (
        <>
          <PromptLine n={item.n} state={props.state}>{stripTrailingGap(cleanPrompt(item.prompt, 'match', locale))}</PromptLine>
          <Tiles
            {...props}
            options={payload.bank.map((entry) => ({ value: entry.slice(0, 1), key: entry.slice(0, 1), label: entry.slice(1).trim() }))}
            keysOnly
          />
        </>
      )
    case 'sort_groups':
      return (
        <>
          <PromptLine n={item.n} state={props.state}>{cleanPrompt(item.prompt, 'sort_groups', locale)}</PromptLine>
          <Tiles {...props} options={payload.groups.map((g) => ({ value: g, label: g }))} inline />
        </>
      )
    case 'order_words':
      return <OrderWords {...props} />
    case 'gap':
    case 'gap_bank':
    case 'transform':
      return <TextItem {...props} />
    default:
      return null
  }
}

/* ── tiles: P/F, a/b/c, odd one out, matching letters, baskets ──────── */

function Tiles({
  options,
  value,
  onChange,
  state,
  disabled,
  solution,
  grid = false,
  inline = false,
  keysOnly = false,
}: BodyProps & {
  options: { value: string; label: string; key?: string }[]
  grid?: boolean
  inline?: boolean
  /** Matching: the key letters are the buttons; the chosen pair is spelled out beside them. */
  keysOnly?: boolean
}) {
  const checked = state === 'correct' || state === 'wrong' || state === 'almost'
  const right = (v: string) => solution.some((s) => s.trim().toLocaleLowerCase('pl') === v.toLocaleLowerCase('pl'))

  if (keysOnly) {
    const chosen = options.find((o) => o.value === value)
    return (
      <div className="flex flex-wrap items-center gap-2">
        {options.map((o) => {
          const picked = o.value === value
          const cls = picked ? (checked ? stateClass[state] : 'is-selected') : checked && right(o.value) ? 'is-missed' : ''
          return (
            <button
              key={o.value}
              type="button"
              disabled={disabled}
              aria-pressed={picked}
              aria-label={`${o.key} ${o.label}`}
              onClick={() => onChange(o.value)}
              className={`cl-answer inline-flex h-10 w-10 items-center justify-center text-class-gloss font-semibold ${cls}`}
            >
              {picked && state === 'correct' ? '✓' : picked && state === 'wrong' ? '✗' : o.key}
            </button>
          )
        })}
        {chosen && (
          <span lang="pl" className={`ml-1 font-class-display ${state === 'wrong' ? 'text-class-wrong line-through' : 'text-class-ink'}`}>
            {chosen.label}
          </span>
        )}
      </div>
    )
  }

  return (
    <div
      className={
        grid ? 'grid gap-3 desk:grid-cols-3' : inline ? 'flex flex-wrap gap-2.5' : 'flex flex-col gap-2.5'
      }
    >
      {options.map((o) => {
        const picked = o.value === value
        const cls = picked ? (checked ? stateClass[state] : 'is-selected') : checked && right(o.value) ? 'is-missed' : ''
        const mark = picked && state === 'correct' ? '✓' : picked && state === 'wrong' ? '✗' : o.key
        return (
          <button
            key={o.value + o.label}
            type="button"
            disabled={disabled}
            aria-pressed={picked}
            onClick={() => onChange(picked && !checked ? '' : o.value)}
            className={[
              'cl-answer flex items-center gap-2.5 text-left',
              inline ? 'min-h-11 px-3.5 py-1.5' : 'min-h-[52px] px-3 py-2',
              cls,
            ].join(' ')}
          >
            {mark && (
              <span
                className={[
                  'inline-flex h-7 min-w-7 shrink-0 items-center justify-center rounded-lg px-1.5 font-class-text text-class-label font-semibold',
                  picked
                    ? state === 'correct'
                      ? 'bg-class-correct text-class-white'
                      : state === 'wrong'
                        ? 'bg-class-wrong text-class-white'
                        : 'bg-class-sky-700 text-class-white'
                    : checked && right(o.value)
                      ? 'bg-class-correct-100 text-class-correct'
                      : 'bg-class-field text-class-sky-700',
                ].join(' ')}
              >
                {mark}
              </span>
            )}
            <span lang="pl" className="font-class-display text-[15px] leading-snug">
              {o.label}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/** Matching: the lettered right-hand column, shown once. */
function BankLegend({ bank }: { bank: string[] }) {
  return (
    <div className="mb-6 flex flex-wrap gap-2">
      {bank.map((entry) => (
        <span key={entry} lang="pl" className="cl-card inline-flex items-center gap-2 px-3 py-1.5 font-class-display text-[15px]">
          <span className="font-class-text text-class-label font-semibold text-class-sky-700">{entry.slice(0, 1)}</span>
          {entry.slice(1).trim()}
        </span>
      ))}
    </div>
  )
}

function WordBank({
  label,
  words,
  used,
  disabled,
  onPick,
}: {
  label: string
  words: string[]
  used: string[]
  disabled: boolean
  onPick: (w: string) => void
}) {
  const left = [...used]
  return (
    <div className="mb-6 flex flex-col gap-2">
      <span className="text-class-label font-semibold text-class-ink-faint">{label}</span>
      <div className="flex flex-wrap gap-2">
        {words.map((w, i) => {
          // A word used once is crossed out once, even when the bank repeats it.
          const at = left.findIndex((u) => u.toLocaleLowerCase('pl') === w.toLocaleLowerCase('pl'))
          const isUsed = at >= 0
          if (isUsed) left.splice(at, 1)
          return (
            <button
              key={`${w}-${i}`}
              type="button"
              disabled={disabled || isUsed}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onPick(w)}
              className={[
                'cl-answer min-h-10 px-3 font-class-display text-[15px]',
                isUsed ? '!border-transparent !bg-class-field-soft text-class-ink-faint line-through' : '',
              ].join(' ')}
            >
              {w}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/* ── text answers: gaps inside the sentence, or one line under it ───── */

function TextItem({ payload, item, value, onChange, state, disabled, locale, onFocusGap, solution }: BodyProps) {
  const prompt = cleanPrompt(item.prompt, payload.type, locale)
  const pieces = prompt.split(GAP)
  const n = pieces.length - 1

  const input = (gap: number, width: string) => {
    const parts = splitGaps(value, Math.max(1, n))
    // Several gaps in one item: once checked, each gap shows its own verdict.
    const own: ItemState =
      n > 1 && (state === 'wrong' || state === 'almost') && solution.length > 0 ? gapState(parts[gap] ?? '', solution, gap) : state
    return (
      <input
        type="text"
        value={parts[gap] ?? ''}
        disabled={disabled}
        onFocus={() => onFocusGap(gap)}
        onChange={(e) => {
          const next = [...parts]
          next[gap] = e.target.value
          onChange(n > 1 ? next.join(GAP_JOIN) : next[0] ?? '')
        }}
        lang="pl"
        data-polish=""
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        aria-label={`${item.n}${n > 1 ? ` (${gap + 1})` : ''}`}
        placeholder="…"
        className={`cl-answer h-11 px-3 font-class-display text-[16px] font-medium outline-none ${width} ${stateClass[own]}`}
      />
    )
  }

  if (n === 0) {
    // „„Jak to się pisze?” → ________” — the answer goes after the arrow, on its own line.
    return (
      <>
        <PromptLine n={item.n} state={state}>{stripTrailingGap(prompt)}</PromptLine>
        {input(0, 'w-full desk:max-w-md')}
      </>
    )
  }
  if (n === 1 && /^\s*$/.test(pieces[1] ?? '') && /(?:→|->)\s*$/.test(pieces[0] ?? '')) {
    return (
      <>
        <PromptLine n={item.n} state={state}>{pieces[0]!.replace(/\s*(?:→|->)\s*$/, '')}</PromptLine>
        {input(0, 'w-full desk:max-w-md')}
      </>
    )
  }
  // „______ (Cześć / Dzień dobry)” — the hint right after a gap belongs under that gap.
  const hints: string[] = []
  const rest = pieces.map((piece, i) => {
    if (i === 0) return piece
    const m = piece.match(/^\s*(\([^)]*\))/)
    hints[i - 1] = m ? m[1]! : ''
    return m ? piece.slice(m[0].length) : piece
  })
  return (
    <p lang="pl" className="m-0 flex flex-wrap items-start gap-x-2 gap-y-3 font-class-display text-class-subheading leading-[44px]">
      <ItemMark n={item.n} state={state} />
      {rest.map((piece, i) => (
        <span key={i} className="contents">
          {piece.trim() && <Hinted text={piece} />}
          {i < n && (
            <span className="inline-flex flex-col gap-0.5 leading-normal">
              {input(i, 'w-44 max-w-full')}
              {hints[i] && (
                <span className="font-class-text text-class-label font-semibold text-class-ink-faint">{hints[i]}</span>
              )}
            </span>
          )}
        </span>
      ))}
    </p>
  )
}

/** „(Cześć / Dzień dobry)” after a gap is a hint: quieter than the sentence. */
function Hinted({ text }: { text: string }) {
  const parts = text.split(/(\([^)]*\))/)
  return (
    <>
      {parts.map((p, i) =>
        /^\([^)]*\)$/.test(p) ? (
          <span key={i} className="font-class-text text-class-label font-semibold text-class-ink-faint">
            {p}
          </span>
        ) : (
          p.trim() && <span key={i}>{p.trim()}</span>
        ),
      )}
    </>
  )
}

/* ── order the words ──────────────────────────────────────────────────── */

function OrderWords({ item, value, onChange, state, disabled, t }: BodyProps) {
  const tiles = useMemo(() => wordTiles(item.prompt), [item.prompt])
  const [picked, setPicked] = useState<number[]>([])
  // Restore from a saved answer only when nothing is being built.
  useEffect(() => {
    if (!value) setPicked([])
  }, [value])
  const update = (next: number[]) => {
    setPicked(next)
    onChange(joinTiles(next.map((i) => tiles[i]!)))
  }
  return (
    <>
      <PromptLine n={item.n} state={state} plain>
        <span className="text-class-gloss text-class-ink-faint">{t.ex.tapWords}</span>
      </PromptLine>
      <div
        className={`cl-answer flex min-h-12 flex-wrap items-center gap-2 px-2.5 py-2 ${picked.length === 0 && !value ? 'cl-slot' : stateClass[state]}`}
      >
        {picked.length === 0 && value ? (
          <span lang="pl" className="font-class-display">
            {value}
          </span>
        ) : picked.length === 0 ? (
          <span className="px-1">…</span>
        ) : (
          picked.map((ti, at) => (
            <button
              key={at}
              type="button"
              disabled={disabled}
              onClick={() => update(picked.filter((_, j) => j !== at))}
              className="cl-answer is-selected min-h-9 px-3 font-class-display"
            >
              {tiles[ti]}
            </button>
          ))
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {tiles.map((w, i) =>
          picked.includes(i) ? null : (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onClick={() => update([...picked, i])}
              className="cl-answer min-h-10 px-3 font-class-display text-[15px]"
            >
              {w}
            </button>
          ),
        )}
      </div>
    </>
  )
}

/* ── open writing: draft saved, model on request, self-check ─────────── */

/**
 * Writing in progress, per exercise, for as long as the page lives.
 *
 * A step used to start from the draft in the cached lesson, which saving
 * never updates: Dalej → Wstecz, or leaving the homework and coming back,
 * showed the old (or an empty) text, and the next keystroke saved THAT over
 * the real draft on the server. Signing out reloads the page, emptying this.
 */
const drafts = new Map<string, string>()

export function OpenTask({ exercise, t }: { exercise: LessonExercise; t: PlayerStrings }) {
  const { next, teacher } = usePlayer()
  const [text, setText] = useState(() => (teacher ? undefined : drafts.get(exercise.id)) ?? String(exercise.attempt?.answers[0] ?? ''))
  // The teacher view opens on the model when the key is shown.
  const [shown, setShown] = useState(Boolean(teacher?.keyShown))
  const [verdict, setVerdict] = useState<'ok' | 'fix' | null>(null)
  const reveal = useMutation({ mutationFn: () => api.course.reveal(exercise.id) })
  const model = exercise.key ?? reveal.data
  // What the server is known to hold.
  const saved = useRef(text)
  const save = useMutation({
    mutationFn: (value: string) => api.course.draft(exercise.id, value),
    onSuccess: (_res, value) => {
      saved.current = value
    },
  })
  const items = itemsOf(exercise.payload)

  // Saved a second after the last keystroke, so a reload never loses writing.
  useEffect(() => {
    if (teacher) return
    drafts.set(exercise.id, text)
    if (text === saved.current) return
    const id = window.setTimeout(() => save.mutate(text), 1000)
    return () => window.clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text])

  // Leaving the step inside that second (Dalej, Wstecz, closing the homework)
  // must not drop the last words: whatever is unsaved goes now.
  useEffect(
    () => () => {
      if (teacher) return
      const latest = drafts.get(exercise.id)
      if (latest !== undefined && latest !== saved.current) void api.course.draft(exercise.id, latest).catch(() => {})
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const stack = useStack()
  usePrimary(stack ? null : { label: `${t.next} ›`, onClick: next })
  const written = text.trim().length > 0
  useEffect(() => {
    stack?.report(exercise.id, { answered: written, checked: written, done: written, score: 0, max: 0, wrong: 0 })
  }, [stack, exercise.id, written])

  return (
    <div className="flex flex-col gap-4">
      {exercise.raw ? (
        <div className="[&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
          <Markdown variant="classroom">{exercise.raw}</Markdown>
        </div>
      ) : (
        items.length > 0 && (
          <ol className="m-0 flex list-none flex-col gap-1.5 p-0">
            {items.map((i) => (
              <li key={i.n} lang="pl">
                <span className="mr-2 text-class-ink-faint">{i.n}.</span>
                {i.prompt}
              </li>
            ))}
          </ol>
        )
      )}
      <textarea
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          setVerdict(null)
        }}
        rows={6}
        lang="pl"
        data-polish=""
        // The teacher view shows the student's writing; it never writes in their name.
        readOnly={Boolean(teacher)}
        placeholder={teacher ? 'Brak tekstu ucznia.' : t.ex.writeHere}
        className={`cl-input min-h-40 font-class-text text-[15px] ${
          verdict === 'ok' ? '!border-class-correct' : verdict === 'fix' ? '!border-class-wrong' : ''
        }`}
      />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-class-label font-semibold text-class-ink-faint">
        <span>{t.ex.words(countWords(text))}</span>
        {save.isSuccess && <span>· {t.ex.saved}</span>}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="secondary"
          onClick={() => {
            if (!model) reveal.mutate()
            setShown((v) => !v)
          }}
        >
          {shown ? t.ex.hideModel : t.ex.showModel}
        </Button>
      </div>
      {shown && model && (
        <div className="flex flex-col gap-3">
          <div className="rounded-class-md bg-class-field-soft px-4 py-3 text-class-ink [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
            <Markdown variant="classroom">{model.rawKey || model.guidance}</Markdown>
          </div>
          <div className={teacher ? 'hidden' : 'flex flex-wrap gap-2.5'}>
            <button
              type="button"
              aria-pressed={verdict === 'ok'}
              onClick={() => setVerdict('ok')}
              className={`cl-btn min-h-10 px-4 text-class-gloss ${verdict === 'ok' ? 'bg-class-correct text-class-white' : 'cl-btn-secondary'}`}
            >
              {verdict === 'ok' ? '✓ ' : ''}
              {t.ex.agrees}
            </button>
            <button
              type="button"
              aria-pressed={verdict === 'fix'}
              onClick={() => setVerdict('fix')}
              className={`cl-btn min-h-10 px-4 text-class-gloss ${verdict === 'fix' ? 'bg-class-wrong text-class-white' : 'cl-btn-secondary'}`}
            >
              {verdict === 'fix' ? '✗ ' : ''}
              {t.ex.needsWork}
            </button>
          </div>
          {verdict === 'fix' && <p className="m-0 text-class-gloss font-bold text-class-wrong">{t.ex.needsWorkHint}</p>}
        </div>
      )}
    </div>
  )
}

/* ── unknown: shown as written, key on request ───────────────────────── */

function RawTask({ exercise, t }: { exercise: LessonExercise; t: PlayerStrings }) {
  const { next, teacher } = usePlayer()
  const [shown, setShown] = useState(Boolean(teacher?.keyShown))
  const reveal = useMutation({ mutationFn: () => api.course.reveal(exercise.id) })
  const model = exercise.key ?? reveal.data
  const stack = useStack()
  usePrimary(stack ? null : { label: `${t.next} ›`, onClick: next })
  useEffect(() => {
    stack?.report(exercise.id, { answered: shown, checked: shown, done: shown, score: 0, max: 0, wrong: 0 })
  }, [stack, exercise.id, shown])
  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-class-md bg-class-field-soft px-4 py-3 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
        <Markdown variant="classroom">{exercise.raw}</Markdown>
      </div>
      <Button
        variant="secondary"
        className="self-start"
        onClick={() => {
          if (!model) reveal.mutate()
          setShown((v) => !v)
        }}
      >
        {shown ? t.ex.hideKey : t.ex.showKey}
      </Button>
      {shown && model && (
        <div className="rounded-class-md bg-class-white px-4 py-3 shadow-[inset_0_0_0_1px_var(--color-class-hairline)] [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
          <Markdown variant="classroom">{model.rawKey}</Markdown>
        </div>
      )}
    </div>
  )
}

export type { CheckResult }
